import React from 'react';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import {
  formatDate,
  formatDateTime,
  getStoreLabel,
  isIssueWarning,
  joinOrDash,
  expectedShipPresentation,
  getItemLineOutcomes,
  getFulfillmentDisplay
} from '../utils/order';

function metric(label, value, detail) {
  return { label, value: value ?? '—', detail };
}

function toneForStatus(status) {
  const value = String(status ?? '').toLowerCase();
  if (value.includes('ship') || value.includes('approved') || value.includes('received')) return 'success';
  if (value.includes('pending') || value.includes('process') || value.includes('released')) return 'warning';
  if (value.includes('cancel') || value.includes('fail') || value.includes('hold')) return 'danger';
  return 'neutral';
}

export function OrderOverview({ context, onOpenCustomization }) {
  const order = context?.order ?? {};
  const signals = context?.operationalSignals ?? {};
  const store = getStoreLabel(context);
  const issueWarnings = (context?.contextWarnings ?? []).filter(isIssueWarning);
  const infoWarnings = (context?.contextWarnings ?? []).filter((x) => !isIssueWarning(x));
  const methods = joinOrDash(context?.payment?.methods ?? []);
  const expectedShip = expectedShipPresentation(order, signals);
  const itemOutcomes = getItemLineOutcomes(context?.items ?? []);

  const metrics = [
    metric('Order status', order.orderStatus, order.workOrderStatus ? `Work order: ${order.workOrderStatus}` : null),
    metric('Payment', context?.payment?.status ?? 'Unknown', methods !== '—' ? methods : 'Method not recorded'),
    metric('Item outcomes', itemOutcomes.value, itemOutcomes.detail),
    metric('Expected ship', expectedShip.value, expectedShip.detail)
  ];

  return (
    <div className="workspace-stack">
      <div className="order-hero">
        <div>
          <div className="eyebrow">Current order</div>
          <div className="order-hero__title-row">
            <h1>{order.orderNo}</h1>
            <StatusBadge tone={toneForStatus(order.orderStatus)}>{order.orderStatus || 'Unknown'}</StatusBadge>
            {store ? <StatusBadge>{store}</StatusBadge> : null}
          </div>
          <p>
            Created {formatDateTime(order.orderDate)} · Shipping {order.shippingType || '—'}
          </p>
        </div>
        <div className="read-only-pill">Read-only intelligence</div>
      </div>

      <div className="metric-grid">
        {metrics.map((item) => (
          <div
            className={`metric-card ${item.label === 'Expected ship' && signals.isPastExpectedShipDate ? 'metric-card--overdue' : ''}`}
            key={item.label}
          >
            <div className="metric-card__label">{item.label}</div>
            <div className="metric-card__value">{item.value}</div>
            {item.detail ? <div className="metric-card__detail">{item.detail}</div> : null}
          </div>
        ))}
      </div>

      {issueWarnings.length ? (
        <SectionCard title="Attention">
          <div className="alert-list">
            {issueWarnings.map((warning) => (
              <div className="alert-row" key={`${warning.code}-${warning.message}`}>
                <StatusBadge tone="warning">{warning.severity}</StatusBadge>
                <div>
                  <strong>{warning.code}</strong>
                  <p>{warning.message}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      ) : null}

      <SectionCard
        title="Items & fulfillment"
        action={signals.hasEmbroidery ? <button className="link-button" onClick={onOpenCustomization}>View customization</button> : null}
      >
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Status</th>
                <th>Fulfillment</th>
                <th>PO</th>
              </tr>
            </thead>
            <tbody>
              {(context?.items ?? []).map((item) => {
                const lifecycle = (context?.itemLifecycle ?? []).find((x) => x.itemNo === item.itemNo);
                return (
                  <tr key={item.itemNo}>
                    <td>#{item.itemNo}</td>
                    <td>
                      <div className="cell-title">{item.styleName || item.styleCode}</div>
                      <div className="cell-subtitle">{[item.colorName, item.sizeName].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td>{item.itemQty}</td>
                    <td><StatusBadge tone={toneForStatus(item.itemStatus)}>{item.itemStatus || 'Unknown'}</StatusBadge></td>
                    <td>{getFulfillmentDisplay(item, lifecycle)}</td>
                    <td>{item.poNumbers || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <div className="two-column-grid">
        <SectionCard title="Purchase orders">
          {(context?.purchaseOrders ?? []).length ? (
            <div className="detail-list">
              {context.purchaseOrders.map((po) => (
                <div className="detail-row" key={po.poNumber}>
                  <div>
                    <strong>{po.poNumber}</strong>
                    <div className="cell-subtitle">Expected {formatDate(po.latestExpectedShipDate)}</div>
                  </div>
                  <div className="detail-row__right">
                    <StatusBadge tone={toneForStatus(po.poItemStatuses)}>{po.poItemStatuses || 'Unknown'}</StatusBadge>
                    <span>{po.poReceivedItemCount ?? 0}/{po.poItemCount ?? 0} received</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <EmptyState text="No purchase-order evidence in this context." />}
        </SectionCard>

        <SectionCard title="Shipment evidence">
          {(context?.tracking ?? []).length ? (
            <div className="detail-list">
              {context.tracking.map((tracking, index) => (
                <div className="detail-row" key={`${tracking.trackingContextType}-${tracking.trackingNo}-${index}`}>
                  <div>
                    <strong>{tracking.trackingContextType === 'CUSTOMER_SHIPMENT' ? 'Customer shipment tracking record' : tracking.trackingContextType}</strong>
                    <div className="cell-subtitle">{tracking.trackingNo || 'Tracking number not recorded'}</div>
                  </div>
                  <span>{formatDateTime(tracking.shipmentDate)}</span>
                </div>
              ))}
            </div>
          ) : <EmptyState text="No shipment tracking evidence in this context." />}
        </SectionCard>
      </div>

      {(context?.returnSummaries ?? []).length ? (
        <SectionCard title="Return activity">
          <div className="return-list">
            {context.returnSummaries.map((item, index) => (
              <div className="return-card" key={`${item.returnSystem}-${item.returnNumber || index}`}>
                <div className="return-card__top">
                  <div>
                    <div className="eyebrow">{friendlyReturnSystem(item.returnSystem)}</div>
                    <strong>{item.returnNumber || 'Return record'}</strong>
                  </div>
                  {item.returnStatus ? (
                    <StatusBadge tone={toneForStatus(item.returnStatus)}>{item.returnStatus}</StatusBadge>
                  ) : null}
                </div>

                <div className="return-facts">
                  {item.returnType ? <ReturnFact label="Type" value={item.returnType} /> : null}
                  {item.returnReason ? <ReturnFact label="Reason" value={item.returnReason} /> : null}
                  {item.totalReturnItems != null ? <ReturnFact label="Return items" value={String(item.totalReturnItems)} /> : null}
                  {item.processStatus ? <ReturnFact label="Process status" value={item.processStatus} /> : null}
                  {item.shopifyReturnStatus ? <ReturnFact label="Shopify return status" value={item.shopifyReturnStatus} /> : null}
                  {item.createdDate ? <ReturnFact label="Created" value={formatDateTime(item.createdDate)} /> : null}
                  {item.processDate ? <ReturnFact label="Processed" value={formatDateTime(item.processDate)} /> : null}
                  {item.closedDate ? <ReturnFact label="Closed" value={formatDateTime(item.closedDate)} /> : null}
                  <ReturnFact label="Delivered" value={item.isDelivered ? 'Yes' : 'No'} />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      ) : null}

      {context?.shopifyEvents?.length ? (
        <SectionCard title="Shopify activity">
          <div className="timeline-list">
            {context.shopifyEvents.map((event, index) => (
              <div className="timeline-row" key={`${event.eventType}-${event.eventDate}-${index}`}>
                <div className="timeline-dot" />
                <div className="timeline-content">
                  <div className="timeline-content__top">
                    <strong>{friendlyEvent(event.eventType)}</strong>
                    <span>{formatDateTime(event.eventDate)}</span>
                  </div>
                  <p>{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      ) : null}

      {infoWarnings.length ? (
        <div className="notices">
          {infoWarnings.map((warning) => (
            <span key={warning.code}><strong>{warning.code}:</strong> {warning.message}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="empty-state compact">{text}</div>;
}

function ReturnFact({ label, value }) {
  return (
    <div className="return-fact">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function friendlyReturnSystem(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return 'Internal return';
  if (raw.toUpperCase() === 'LEGACY_RMA') return 'Legacy RMA';

  return raw
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function friendlyEvent(eventType) {
  return String(eventType ?? '')
    .replace(/^SHOPIFY_/, '')
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
